import React, { useState, useEffect } from "react";
import {
  Stack,
  Alert,
  Button,
  Text,
  Loader,
  Center,
  Title,
  Box,
  SimpleGrid,
  Paper,
  Group,
  ThemeIcon,
} from "@mantine/core";
import { IconCalendarEvent, IconAlertCircle } from "@tabler/icons-react";
import { API_BASE_URL } from "../../../config/baseUrls";

export default function EventSelectionStep({
  formData,
  setFormData,
  onNext,
  onBack,
}) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const res = await fetch(
          `${API_BASE_URL}/shop-owner/events/active`,
        );
        const json = await res.json();

        if (json.success) {
          setEvents(json.data);
        } else {
          setError("Could not load events.");
        }
      } catch (err) {
        setError("Network error fetching events.");
      } finally {
        setLoading(false);
      }
    };
    fetchEvents();
  }, []);

  return (
    <Stack gap="xl">
      {/* 1. Clear, plain-English instructions */}
      <Box>
        <Title order={3} size="h2" mb="xs">
          Which event are you attending?
        </Title>
        <Text size="lg" c="dimmed">
          Select the physical event where your booth will be located. This
          ensures your products show up on the official event page for customers
          to see.
        </Text>
      </Box>

      {/* 2. Loading State */}
      {loading && (
        <Center p="xl" mih={200}>
          <Stack align="center" gap="sm">
            <Loader size="lg" color="blue" />
            <Text size="lg" c="dimmed">
              Finding active events...
            </Text>
          </Stack>
        </Center>
      )}

      {/* 3. Error State */}
      {error && !loading && (
        <Alert
          icon={<IconAlertCircle size={24} />}
          color="red"
          radius="md"
          size="lg"
        >
          <Text size="md" fw={600}>
            {error}
          </Text>
          <Text size="sm">Please refresh the page or try again later.</Text>
        </Alert>
      )}

      {/* 4. Card-Based Options (Replacing the Dropdown) */}
      {!loading && !error && (
        <Box>
          {events.length === 0 ? (
            <Paper withBorder p="xl" radius="md" ta="center" bg="gray.0">
              <Text size="lg" c="dimmed">
                There are currently no active events available for registration.
              </Text>
            </Paper>
          ) : (
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="lg">
              {events.map((event) => {
                const isSelected = formData.linkedProductId === event.value;

                return (
                  <Paper
                    key={event.value}
                    withBorder
                    p="xl"
                    radius="md"
                    onClick={() =>
                      setFormData((prev) => ({
                        ...prev,
                        linkedProductId: event.value,
                      }))
                    }
                    style={{
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      borderColor: isSelected
                        ? "var(--mantine-color-blue-6)"
                        : "var(--mantine-color-gray-3)",
                      backgroundColor: isSelected
                        ? "var(--mantine-color-blue-0)"
                        : "white",
                    }}
                    className="hover:shadow-md"
                  >
                    <Group gap="md" wrap="nowrap" align="center">
                      <ThemeIcon
                        size={56}
                        radius="md"
                        color={isSelected ? "blue" : "gray"}
                        variant={isSelected ? "filled" : "light"}
                      >
                        <IconCalendarEvent size={28} />
                      </ThemeIcon>
                      <Box>
                        <Title
                          order={4}
                          size="h4"
                          c={isSelected ? "blue.9" : "dark.9"}
                        >
                          {event.label}
                        </Title>
                        <Text size="sm" c="dimmed" mt={4}>
                          Click to select this venue
                        </Text>
                      </Box>
                    </Group>
                  </Paper>
                );
              })}
            </SimpleGrid>
          )}
        </Box>
      )}

      {/* 5. Navigation Buttons with increased sizes */}
      <Group
        justify="space-between"
        mt="xl"
        pt="md"
        style={{ borderTop: "1px solid var(--mantine-color-gray-2)" }}
      >
        <Button size="lg" variant="default" onClick={onBack}>
          Go Back
        </Button>
        <Button
          size="lg"
          onClick={onNext}
          disabled={!formData.linkedProductId || loading}
        >
          Save & Continue
        </Button>
      </Group>
    </Stack>
  );
}
